import { useAuth0 } from "@auth0/auth0-react"
import { UiSurface, UiSurfaceId } from "shared/ui/uiSurface"
import * as Sentry from "@sentry/react"
import { FC } from "react"
import { BrowserRouter, Route, RouteObject, Routes } from "react-router"
import { ConfigurationPage } from "./app/components/configuration/ConfigurationPage"
import { CustomersPage } from "./app/components/customers/CustomersPage"
import { DashboardPage } from "./app/components/dashboard/DashboardPage"
import { EmployeesPage } from "./app/components/employees/EmployeesPage"
import { MainLayout } from "./app/components/MainLayout"
import { NotFoundPage } from "./app/components/NotFoundPage"
import { OrderDetailPage } from "./app/components/OrderDetailPage"
import { OrdersPage } from "./app/components/orders/OrdersPage"
import { ProfilePage } from "./app/components/profile/ProfilePage"
import { AddressesPage } from "./app/components/addresses/AddressesPage"
import { CourierLinksPage } from "./app/components/links/CourierLinksPage"
import { CouriersPage } from "./app/components/couriers/CouriersPage"
import { RecurringOrdersPage } from "./app/components/recurringOrders/RecurringOrdersPage"
import { ReportsPage } from "./app/components/reports/ReportsPage"
import { ProtectedRoute } from "./app/components/ProtectedRoute"
import { PublicHomePage } from "./app/components/PublicHomePage"
import { WorkInProgressPage } from "./app/components/WorkInProgressPage"
import { pathConstants } from "./app/pathConstants"
import { useGetProfileQuery } from "./features/profile/profileApi"

const SentryRoutes = import.meta.env.VITE_DISABLE_SENTRY
  ? Routes
  : Sentry.withSentryReactRouterV7Routing(Routes)

export type MobilusRouteObject = RouteObject & {
  isPublic?: boolean
  ui?: UiSurfaceId
}

/** Legacy webapp screens routed ahead of their feature (DEV-394). */
const wipRoutes: { path: string; ui: UiSurfaceId; titleKey: string }[] = [
  {
    path: pathConstants.dashboardLockers,
    ui: "UI-DLV2-202",
    titleKey: "nav.lockerKpis",
  },
  { path: pathConstants.lockers, ui: "UI-DLV2-200", titleKey: "nav.lockers" },
  {
    path: pathConstants.pickToLight,
    ui: "UI-DLV2-201",
    titleKey: "nav.pickToLight",
  },
  {
    path: pathConstants.routePlanning,
    ui: "UI-DLV2-203",
    titleKey: "nav.routePlanning",
  },
  { path: pathConstants.billing, ui: "UI-DLV2-204", titleKey: "nav.billing" },
  { path: pathConstants.support, ui: "UI-DLV2-205", titleKey: "nav.support" },
]

export const allRoutes: MobilusRouteObject[] = [
  {
    path: `${pathConstants.login}`,
    isPublic: true,
    ui: "UI-DLV2-001",
    element: <PublicHomePage />,
  },
  {
    path: `/`,
    isPublic: false,
    ui: "UI-DLV2-020",
    element: (
      <MainLayout>
        <OrdersPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.dashboard}`,
    isPublic: false,
    ui: "UI-DLV2-070",
    element: (
      <MainLayout>
        <DashboardPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.orders}`,
    isPublic: false,
    ui: "UI-DLV2-020",
    element: (
      <MainLayout>
        <OrdersPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.orderDetail}`,
    isPublic: false,
    ui: "UI-DLV2-050",
    element: <OrderDetailPage />,
  },
  {
    path: `${pathConstants.employees}`,
    isPublic: false,
    ui: "UI-DLV2-110",
    element: (
      <MainLayout>
        <EmployeesPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.customers}`,
    isPublic: false,
    ui: "UI-DLV2-080",
    element: (
      <MainLayout>
        <CustomersPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.profile}`,
    isPublic: false,
    ui: "UI-DLV2-160",
    element: (
      <MainLayout>
        <ProfilePage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.configuration}/*`,
    isPublic: false,
    ui: "UI-DLV2-170",
    element: (
      <MainLayout>
        <ConfigurationPage />
      </MainLayout>
    ),
  },
  {
    path: pathConstants.recurringOrders,
    isPublic: false,
    ui: "UI-DLV2-140",
    element: (
      <MainLayout>
        <RecurringOrdersPage />
      </MainLayout>
    ),
  },
  {
    path: pathConstants.addresses,
    isPublic: false,
    ui: "UI-DLV2-150",
    element: (
      <MainLayout>
        <AddressesPage />
      </MainLayout>
    ),
  },
  {
    path: pathConstants.links,
    isPublic: false,
    ui: "UI-DLV2-130",
    element: (
      <MainLayout>
        <CourierLinksPage />
      </MainLayout>
    ),
  },
  {
    path: pathConstants.couriers,
    isPublic: false,
    ui: "UI-DLV2-120",
    element: (
      <MainLayout>
        <CouriersPage />
      </MainLayout>
    ),
  },
  {
    path: `${pathConstants.reports}/*`,
    isPublic: false,
    ui: "UI-DLV2-190",
    element: (
      <MainLayout>
        <ReportsPage />
      </MainLayout>
    ),
  },
  ...wipRoutes.map(
    ({ path, ui, titleKey }): MobilusRouteObject => ({
      path,
      isPublic: false,
      ui,
      element: (
        <MainLayout>
          <WorkInProgressPage titleKey={titleKey} />
        </MainLayout>
      ),
    }),
  ),
  {
    path: "*",
    ui: "UI-DLV2-003",
    element: <NotFoundPage />,
  },
]

const renderRoutes = (routes: MobilusRouteObject[], props: any) => {
  return routes.map((route, index) => {
    const routeElement = route.ui ? (
      <UiSurface id={route.ui}>{route.element}</UiSurface>
    ) : (
      route.element
    )
    const element = route.isPublic ? (
      routeElement
    ) : (
      <ProtectedRoute {...props}>{routeElement}</ProtectedRoute>
    )
    return (
      <Route key={index} path={route.path} element={element}>
        {route.children && renderRoutes(route.children, props)}
      </Route>
    )
  })
}

export const AppRoutes: FC = () => {
  const auth0 = useAuth0()
  const profile = useGetProfileQuery(null, {
    skip: !auth0?.isAuthenticated,
  })
  const isLoading = auth0.isLoading || profile.isLoading
  const isAuthenticated = auth0.isAuthenticated
  const hasError = !!auth0.error

  if (hasError) {
    return (
      <BrowserRouter>
        <SentryRoutes>
          <Route
            path="*"
            element={
              <UiSurface id="UI-DLV2-001">
                <PublicHomePage />
              </UiSurface>
            }
          />
        </SentryRoutes>
      </BrowserRouter>
    )
  }

  const authProps = { isLoading, isAuthenticated }

  return (
    <BrowserRouter>
      <SentryRoutes>{renderRoutes(allRoutes, authProps)}</SentryRoutes>
    </BrowserRouter>
  )
}
